import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  Clock,
  Coins,
  DollarSign,
  FileCheck2,
  Link as LinkIcon,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button, ButtonRouter } from "~/components/ui/button";
import { PageHeader } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/cn";
import {
  getRefreshedCoachFn,
  connectCoachToStripeFn,
} from "~/lib/functions/stripe-connect";

export const Route = createFileRoute("/_user/my-coaching/stripe-connect")({
  validateSearch: z.object({
    accountId: z.string().optional(),
  }),
  staticData: { title: "Payment setup" },
  loader: async ({ context, location }) => {
    const { accountId } = (location.search ?? {}) as { accountId?: string };

    await context.queryClient.ensureQueryData({
      queryKey: ["coach-stripe-data", accountId],
      queryFn: () => getRefreshedCoachFn({ data: { accountId } }),
    });
    return {};
  },
  component: StripeConnectPage,
});

const CIRCUMFERENCE = 2 * Math.PI * 28;

function StripeConnectPage() {
  const { accountId } = Route.useSearch();

  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["coach-stripe-data", accountId],
    queryFn: () => getRefreshedCoachFn({ data: { accountId } }),
  });

  // When Stripe state was synced to DB, invalidate the coach profile cache
  // so alert cards on other pages reflect the updated state
  useEffect(() => {
    if (data?.synced) {
      queryClient.invalidateQueries({ queryKey: ["my-coaching-profile"] });
    }
  }, [data?.synced, queryClient]);

  const connectMutation = useMutation({
    mutationFn: () => connectCoachToStripeFn(),
    onSuccess: (response) => {
      toast.success("Redirecting to Stripe Connect...");
      window.open(response.url, "_blank");
    },
    onError: () => {
      toast.error("Failed to connect to Stripe");
    },
  });

  const startOnboarding = () => connectMutation.mutate();

  if (!data) {
    return (
      <div className="max-w-4xl space-y-8">
        <div className="flex items-center justify-between gap-6">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="size-16 rounded-full" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="size-10 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-64" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const stripeConnected = !!data.coach.stripeAccountId;
  const stripeDetailsSubmitted = data.account?.details_submitted ?? false;

  const stripeVerificationPending = () => {
    const pendingVerification =
      data.account?.requirements?.pending_verification ?? [];
    return pendingVerification.length > 0;
  };

  const stripeHasCurrentlyDue = () => {
    const currentlyDue = data.account?.requirements?.currently_due ?? [];
    return currentlyDue.length > 0;
  };

  const stripePastDue = () => {
    const pastDue = data.account?.requirements?.past_due ?? [];
    return pastDue.length > 0;
  };

  const stripeDisabledReason = () => {
    return data.account?.requirements?.disabled_reason;
  };

  const stripeChargesActive = !!data.account?.charges_enabled;
  const stripePayoutsActive = !!data.account?.payouts_enabled;

  const contractCompleted = !!data.coach.contractSigned;
  const kycCompleted = !stripeHasCurrentlyDue();

  const steps = [
    { key: "contract", completed: contractCompleted },
    { key: "account", completed: stripeConnected },
    { key: "details", completed: stripeDetailsSubmitted },
    { key: "kyc", completed: kycCompleted },
    { key: "charges", completed: stripeChargesActive },
    { key: "payouts", completed: stripePayoutsActive },
  ] as const;

  const totalSteps = steps.length;
  const completedCount = steps.filter((s) => s.completed).length;
  const activeStepIndex = steps.findIndex((s) => !s.completed);
  const allComplete = completedCount === totalSteps;
  const disabled = !data.coach.contractSigned;

  const humanize = (key: string) =>
    key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const requirements = data.account?.requirements;
  const connectLabel = (idle: string) =>
    connectMutation.isPending ? "Loading..." : idle;

  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        title="Payment setup"
        description={
          allComplete
            ? "You're all set to accept payments"
            : `${totalSteps - completedCount} step${totalSteps - completedCount > 1 ? "s" : ""} remaining`
        }
        actions={
          <div className="relative flex size-16 shrink-0 items-center justify-center">
            <svg
              className="absolute h-full w-full -rotate-90"
              viewBox="0 0 64 64"
              aria-hidden
            >
              <circle
                cx="32"
                cy="32"
                r="28"
                strokeWidth="4"
                className="fill-none stroke-gray-100"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                strokeWidth="4"
                className="fill-none stroke-purple-700 transition-[stroke-dashoffset] duration-500"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={
                  CIRCUMFERENCE * (1 - completedCount / totalSteps)
                }
                strokeLinecap="round"
              />
            </svg>
            <span className="text-sm font-medium text-gray-900 tabular-nums">
              {completedCount}/{totalSteps}
            </span>
          </div>
        }
      />

      {allComplete && (
        <div className="space-y-4 rounded-2xl border border-purple-200 bg-purple-50 p-6 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-purple-700 text-white">
            <Check className="size-6" />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-lg font-medium text-gray-900">
              Payment setup complete
            </h2>
            <p className="text-text-secondary text-sm">
              You can now create coaching programs and send invoices.
            </p>
          </div>
          <div className="flex justify-center gap-2">
            <ButtonRouter to="/my-coaching/clients/invoices">
              Go to invoices
            </ButtonRouter>
            <Button variant="ghost" onClick={startOnboarding}>
              Manage Stripe settings
            </Button>
          </div>
        </div>
      )}

      <ol className="max-w-prose">
        <Step
          icon={FileCheck2}
          done={contractCompleted}
          title="Sign your coach contract"
          active={activeStepIndex === 0}
          pendingTone="amber"
        >
          <p className="text-text-secondary text-sm">
            Please check your email for the contract we sent you. You need to
            sign the coach contract before proceeding with payment setup.
          </p>
        </Step>

        <Step
          icon={LinkIcon}
          done={stripeConnected}
          title="Create Stripe account"
          doneTitle="Stripe account created"
          active={activeStepIndex === 1}
          disabled={disabled}
        >
          <p className="text-text-secondary text-sm">
            Create a Stripe account to start sending invoices.
          </p>
          {activeStepIndex === 1 && (
            <Button
              onClick={startOnboarding}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending
                ? "Connecting..."
                : "Connect with Stripe"}
            </Button>
          )}
        </Step>

        <Step
          icon={FileCheck2}
          done={stripeDetailsSubmitted}
          title="Submit your details"
          active={activeStepIndex === 2}
          disabled={disabled}
        >
          <p className="text-text-secondary text-sm">
            Complete your account setup by submitting your details.
          </p>
          {activeStepIndex === 2 && stripeConnected && (
            <Button
              onClick={startOnboarding}
              disabled={connectMutation.isPending}
            >
              {connectLabel("Complete onboarding")}
            </Button>
          )}
        </Step>

        <Step
          icon={ShieldCheck}
          done={kycCompleted}
          title="Additional KYC verification"
          active={activeStepIndex === 3}
          disabled={disabled}
        >
          <p className="text-text-secondary text-sm">
            We'll need some more information to comply with regulations before
            we can activate payouts.
          </p>
          {requirements?.currently_due &&
            requirements.currently_due.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {requirements.currently_due.map((requirement) => (
                  <Badge
                    key={requirement}
                    size="sm"
                    variant="outline"
                    className={AMBER_CHIP}
                  >
                    <Clock />
                    {requirement}
                  </Badge>
                ))}
              </div>
            )}
        </Step>

        <Step
          icon={DollarSign}
          done={stripeChargesActive}
          title="Charges enabled"
          active={activeStepIndex === 4}
          disabled={disabled}
          doneContent={
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.account?.capabilities ?? {})
                .filter(([, capability]) => capability === "active")
                .map(([key]) => (
                  <Badge key={key} size="sm" variant="success">
                    <Check />
                    {humanize(key)}
                  </Badge>
                ))}
            </div>
          }
        >
          <p className="text-text-secondary text-sm">
            {!stripeDetailsSubmitted
              ? "First submit your account details to enable charges."
              : stripePastDue()
                ? "Some required information is past due. Please provide this to re-enable charges."
                : stripeVerificationPending()
                  ? "We're verifying your submitted information."
                  : stripeHasCurrentlyDue()
                    ? "Additional information is needed to enable charges."
                    : "Next, we'll enable charges so you can start sending invoices."}
          </p>

          {stripePastDue() && (
            <Callout
              tone="red"
              icon={AlertTriangle}
              title="Past due requirements"
              description="The following information is past due. Please provide this as soon as possible:"
            >
              <div className="flex flex-wrap gap-2">
                {requirements?.past_due?.map((requirement) => (
                  <Badge key={requirement} size="sm" variant="danger">
                    <AlertTriangle />
                    {humanize(requirement)}
                  </Badge>
                ))}
              </div>
            </Callout>
          )}

          {stripeHasCurrentlyDue() && !stripePastDue() && (
            <Callout
              tone="purple"
              icon={AlertTriangle}
              title="Required information"
              description={
                requirements?.current_deadline
                  ? `Please provide the following by ${new Date(
                      requirements.current_deadline,
                    ).toLocaleDateString()}:`
                  : "Please provide the following information to enable charges:"
              }
            >
              <div className="flex flex-wrap gap-2">
                {requirements?.currently_due?.map((requirement) => (
                  <Badge key={requirement} size="sm" variant="secondary">
                    <LinkIcon />
                    {humanize(requirement)}
                  </Badge>
                ))}
              </div>
            </Callout>
          )}

          {stripeVerificationPending() && (
            <Callout
              tone="amber"
              icon={Clock}
              title="Verification in progress"
              description="We're reviewing your submitted information. This usually takes 24-48 hours. You'll receive an email once the verification is complete."
            >
              <div className="flex flex-wrap gap-2">
                {requirements?.pending_verification?.map((requirement) => (
                  <Badge
                    key={requirement}
                    size="sm"
                    variant="outline"
                    className={AMBER_CHIP}
                  >
                    <Clock />
                    {humanize(requirement)}
                  </Badge>
                ))}
              </div>
            </Callout>
          )}

          {stripeDisabledReason() && (
            <Callout
              tone="gray"
              icon={AlertTriangle}
              title="Account status"
              description={
                stripeDisabledReason() === "requirements.past_due"
                  ? "Your account is temporarily disabled because some required information is past due."
                  : stripeDisabledReason() ===
                      "requirements.pending_verification"
                    ? "Your account is temporarily disabled while we verify your information."
                    : `Your account is currently disabled (${stripeDisabledReason()}). Please contact support if you need help.`
              }
            />
          )}

          {stripeConnected && !stripeDetailsSubmitted && (
            <Button
              onClick={startOnboarding}
              disabled={connectMutation.isPending}
            >
              {connectLabel("Submit your account details")}
            </Button>
          )}
          {stripeConnected &&
            stripeDetailsSubmitted &&
            (stripeHasCurrentlyDue() || stripePastDue()) &&
            !stripeVerificationPending() && (
              <Button
                onClick={startOnboarding}
                variant={stripePastDue() ? "destructive" : "default"}
                disabled={connectMutation.isPending}
              >
                {connectLabel(
                  stripePastDue()
                    ? "Provide past due information"
                    : "Complete required information",
                )}
              </Button>
            )}
        </Step>

        <Step
          icon={Coins}
          done={stripePayoutsActive}
          title="Payouts active"
          active={activeStepIndex === 5}
          disabled={disabled}
          last
        >
          <p className="text-text-secondary text-sm">
            Finally, we need to make sure you can receive payments.
          </p>
          {activeStepIndex === 5 &&
            stripeChargesActive &&
            !stripePayoutsActive && (
              <Button
                onClick={startOnboarding}
                disabled={connectMutation.isPending}
              >
                {connectLabel("Finish your setup in Stripe")}
              </Button>
            )}
        </Step>
      </ol>
    </div>
  );
}

const AMBER_CHIP = "border-amber-200 bg-amber-50 text-amber-800";

/**
 * One row of the setup timeline: a status dot on a rail, then the step. A
 * finished step shrinks to a muted title; the step the coach is on sits in a
 * purple inset.
 */
function Step({
  icon: Icon,
  done,
  title,
  doneTitle = title,
  active,
  disabled,
  last,
  pendingTone,
  doneContent,
  children,
}: {
  icon: LucideIcon;
  done: boolean;
  title: string;
  doneTitle?: string;
  active: boolean;
  disabled?: boolean;
  last?: boolean;
  /** Tone of the dot while the step is open; the first step nudges in amber. */
  pendingTone?: "amber";
  doneContent?: ReactNode;
  children: ReactNode;
}) {
  return (
    <li
      className={cn(
        "relative flex gap-4",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full",
            done
              ? "bg-purple-100 text-purple-700"
              : pendingTone === "amber"
                ? "bg-amber-100 text-amber-800"
                : "text-text-tertiary bg-gray-100",
          )}
        >
          {done ? <Check className="size-4" /> : <Icon className="size-4" />}
        </div>
        {!last && (
          <div
            className={cn(
              "mt-1 w-px flex-1",
              done ? "bg-purple-200" : "bg-gray-200",
            )}
          />
        )}
      </div>
      <div className="flex-1 pb-8">
        {done ? (
          <div className="space-y-2">
            <h3 className="text-text-tertiary text-base font-medium">
              {doneTitle}
            </h3>
            {doneContent}
          </div>
        ) : (
          <div
            className={cn("space-y-2", active && "rounded-xl bg-purple-50 p-4")}
          >
            <h3 className="text-base font-medium text-gray-900">{title}</h3>
            {children}
          </div>
        )}
      </div>
    </li>
  );
}

const CALLOUT_TONES = {
  red: { box: "border-red-200 bg-red-50", text: "text-red-700" },
  purple: { box: "border-purple-200 bg-purple-50", text: "text-purple-700" },
  amber: { box: "border-amber-200 bg-amber-50", text: "text-amber-800" },
  gray: { box: "border-gray-200 bg-gray-50", text: "text-gray-700" },
} as const;

function Callout({
  tone,
  icon: Icon,
  title,
  description,
  children,
}: {
  tone: keyof typeof CALLOUT_TONES;
  icon: LucideIcon;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  const { box, text } = CALLOUT_TONES[tone];
  return (
    <div className={cn("space-y-2 rounded-2xl border p-4", box)}>
      <div className={cn("flex items-center gap-2", text)}>
        <Icon className="size-5" />
        <span className="font-medium">{title}</span>
      </div>
      <p className={cn("text-sm", text)}>{description}</p>
      {children}
    </div>
  );
}
